import { LoiMayChu } from './api';

export type AccessErrorKind =
  | 'UNAUTHENTICATED'      // 401: session expired or invalid token
  | 'PAYMENT_REQUIRED'     // 402: unpaid / no active plan
  | 'SUBSCRIPTION_EXPIRED' // 402: plan expired, needs renewal
  | 'MEMBERSHIP_FORBIDDEN' // 403: permission / role boundary (e.g. ADMIN_REQUIRED), NOT an upsell!
  | 'QUOTA_EXCEEDED'       // 429: daily or rate limits reached
  | 'GENERIC_ERROR';

export interface UpgradeGuidance {
  shouldUpsell: boolean;
  isExpired: boolean;
  title: string;
  message: string;
  planName?: string | null;
  expiresAt?: string | null;
}

export interface QuotaRecoveryNotice {
  canRecover: boolean;
  message: string;
  limit: number | null;
  used: number;
  remaining: number | null;
}

interface ErrorWithStatusOrCode {
  status?: number;
  trangThai?: number;
  code?: string | null;
  ma?: string | null;
  message?: string;
  dulieu?: unknown;
  data?: unknown;
}

function extractErrorInfo(err: unknown): { status: number; code: string; message: string; data?: unknown } {
  if (err instanceof LoiMayChu) {
    return {
      status: err.trangThai,
      code: String(err.ma || '').toUpperCase(),
      message: err.message,
      data: err.dulieu,
    };
  }
  if (typeof err === 'object' && err !== null) {
    const e = err as ErrorWithStatusOrCode;
    const status = Number(e.trangThai || e.status || 0);
    const code = String(e.ma || e.code || '').toUpperCase();
    const message = typeof e.message === 'string' ? e.message : '';
    const data = e.dulieu || e.data;
    return { status, code, message, data };
  }
  return { status: 0, code: '', message: String(err || '') };
}

/**
 * Classifies an API error according to permission and billing boundaries.
 */
export function classifyAccessError(err: unknown): AccessErrorKind {
  const { status, code } = extractErrorInfo(err);

  if (status === 401 || code === 'UNAUTHORIZED' || code === 'TOKEN_EXPIRED') {
    return 'UNAUTHENTICATED';
  }

  if (status === 402 || code === 'PAYMENT_REQUIRED' || code === 'SUBSCRIPTION_EXPIRED') {
    if (code === 'SUBSCRIPTION_EXPIRED') return 'SUBSCRIPTION_EXPIRED';
    return 'PAYMENT_REQUIRED';
  }

  // 403 represents membership or administrative permission denial
  if (status === 403 || code === 'ADMIN_REQUIRED' || code === 'FORBIDDEN') {
    return 'MEMBERSHIP_FORBIDDEN';
  }

  if (status === 429 || code === 'QUOTA_EXCEEDED') {
    return 'QUOTA_EXCEEDED';
  }

  return 'GENERIC_ERROR';
}

/**
 * Returns structured upgrade guidance only for legitimate billing/subscription events (402).
 * Strictly returns null for 403 (membership denial), preventing false upsell.
 */
export function getUpgradeGuidance(err: unknown): UpgradeGuidance | null {
  const kind = classifyAccessError(err);
  const { message, data } = extractErrorInfo(err);
  const payloadData = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>;

  if (kind === 'SUBSCRIPTION_EXPIRED') {
    return {
      shouldUpsell: true,
      isExpired: true,
      title: 'Gói dịch vụ đã hết hạn',
      message: message || 'Gói dịch vụ đã hết hạn. Vui lòng gia hạn để tiếp tục sử dụng.',
      planName: (payloadData.planName as string) || null,
      expiresAt: (payloadData.expiresAt as string) || null,
    };
  }

  if (kind === 'PAYMENT_REQUIRED') {
    return {
      shouldUpsell: true,
      isExpired: false,
      title: 'Cần kích hoạt gói dịch vụ',
      message: message || 'Vui lòng chọn gói và hoàn tất đăng ký trước khi sử dụng Dashboard.',
    };
  }

  return null;
}

/**
 * Returns quota recovery information for rate/quota exceeded errors (429).
 */
export function getQuotaRecoveryNotice(err: unknown): QuotaRecoveryNotice | null {
  const kind = classifyAccessError(err);
  if (kind !== 'QUOTA_EXCEEDED') return null;

  const { message, data } = extractErrorInfo(err);
  const payloadData = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>;

  const limit = typeof payloadData.limit === 'number' ? payloadData.limit : null;
  const used = typeof payloadData.used === 'number' ? payloadData.used : 0;
  const remaining = typeof payloadData.remaining === 'number' ? payloadData.remaining : null;

  return {
    canRecover: true,
    message: message || 'Hạn mức hôm nay đã hết. Bạn có thể thử lại vào ngày mai (00:00) hoặc nâng cấp gói.',
    limit,
    used,
    remaining,
  };
}

/**
 * Helper to check whether error is a membership / administrative permission failure.
 * Must NOT trigger pricing upsell modals.
 */
export function isMembershipForbidden(err: unknown): boolean {
  return classifyAccessError(err) === 'MEMBERSHIP_FORBIDDEN';
}

/**
 * Helper to check whether error is a subscription / payment failure (402).
 */
export function isSubscriptionRequired(err: unknown): boolean {
  const kind = classifyAccessError(err);
  return kind === 'PAYMENT_REQUIRED' || kind === 'SUBSCRIPTION_EXPIRED';
}

/**
 * Checks whether an error string contains sensitive API or infrastructure details
 * (e.g. database exceptions, secret tokens, execution traces, or raw JSON payloads).
 */
export function isSensitiveError(message: unknown): boolean {
  if (typeof message !== 'string' || !message.trim()) return true;
  const str = message.trim();
  const lower = str.toLowerCase();

  // Raw JSON payloads
  if (str.startsWith('{') || str.startsWith('[') || lower.includes('{"code"') || lower.includes('"error"')) {
    return true;
  }

  // Stack traces or execution dumps
  if (/at\s+[\w$./<>]+\s*\(?.*:\d+:\d+\)?/.test(str) || /traceback/i.test(str) || /syntaxerror/i.test(str)) {
    return true;
  }

  // Sensitive security, database, and infrastructure markers
  const sensitivePatterns = [
    'bearer ',
    'token',
    'secret',
    'cookie',
    'jwt',
    'sqlite',
    'database error',
    'sql error',
    'select *',
    'insert into',
    'internal server error',
    'econnrefused',
    'password',
    '[object object]',
    'undefined',
    'null pointer',
  ];

  return sensitivePatterns.some((pattern) => lower.includes(pattern));
}

/**
 * Sanitizes an error for user display, preventing raw or sensitive API internals from being leaked.
 */
export function sanitizeUserErrorMessage(
  err: unknown,
  fallback = 'Không gửi được yêu cầu. Vui lòng thử lại sau.'
): string {
  if (err instanceof LoiMayChu) {
    if (err.trangThai >= 500) {
      return 'Máy chủ gặp sự cố nội bộ. Vui lòng thử lại sau.';
    }
    if (err.message && !isSensitiveError(err.message)) {
      return err.message;
    }
    return fallback;
  }

  if (err instanceof Error) {
    if (err.message && !isSensitiveError(err.message)) {
      return err.message;
    }
    return fallback;
  }

  if (typeof err === 'string' && !isSensitiveError(err)) {
    return err;
  }

  return fallback;
}

export interface SubmitErrorResolution {
  kind: AccessErrorKind;
  userMessage: string;
  shouldOpenUpgradeModal: boolean;
  canRecover: boolean;
}

/**
 * Resolves caught submission errors for TaoBai and post workflows:
 * - 402 PAYMENT_REQUIRED / SUBSCRIPTION_EXPIRED: invokes onYeuCauNangCap callback for existing upgrade modal.
 * - 429 QUOTA_EXCEEDED: returns clear actionable recovery guidance; clears existing message via UI dismissal.
 * - 403 MEMBERSHIP_FORBIDDEN: role/permission boundary, strictly NOT an upsell.
 * - Never renders raw or sensitive API errors.
 */
export function resolveSubmitAccessError(
  err: unknown,
  onYeuCauNangCap?: () => void
): SubmitErrorResolution {
  const kind = classifyAccessError(err);

  if (kind === 'PAYMENT_REQUIRED' || kind === 'SUBSCRIPTION_EXPIRED') {
    const guidance = getUpgradeGuidance(err);
    const safeMessage = guidance?.message && !isSensitiveError(guidance.message)
      ? guidance.message
      : (kind === 'SUBSCRIPTION_EXPIRED'
          ? 'Gói dịch vụ đã hết hạn. Vui lòng gia hạn để tiếp tục sử dụng.'
          : 'Vui lòng chọn gói và hoàn tất đăng ký trước khi sử dụng Dashboard.');

    if (onYeuCauNangCap) {
      onYeuCauNangCap();
    }

    return {
      kind,
      userMessage: safeMessage,
      shouldOpenUpgradeModal: true,
      canRecover: true,
    };
  }

  if (kind === 'QUOTA_EXCEEDED') {
    const recovery = getQuotaRecoveryNotice(err);
    const safeMessage = recovery?.message && !isSensitiveError(recovery.message)
      ? recovery.message
      : 'Hạn mức hôm nay đã hết. Bạn có thể thử lại vào ngày mai (00:00) hoặc nâng cấp gói.';

    return {
      kind,
      userMessage: safeMessage,
      shouldOpenUpgradeModal: false,
      canRecover: true,
    };
  }

  if (kind === 'MEMBERSHIP_FORBIDDEN') {
    // 403 permission / membership refusal: MUST NOT trigger upgrade modal or upsell
    let safeMessage = 'Bạn không có quyền thực hiện thao tác này.';
    if (err instanceof LoiMayChu && err.message && !isSensitiveError(err.message)) {
      safeMessage = err.message;
    } else if (err instanceof Error && err.message && !isSensitiveError(err.message)) {
      safeMessage = err.message;
    }

    return {
      kind,
      userMessage: safeMessage,
      shouldOpenUpgradeModal: false,
      canRecover: false,
    };
  }

  return {
    kind,
    userMessage: sanitizeUserErrorMessage(err),
    shouldOpenUpgradeModal: false,
    canRecover: false,
  };
}
